import type { IdentityDiscoveryConfig, PixelConfig } from "./types";

type Form = {
  getAttribute: (name: string) => string | null;
  ownerDocument?: { baseURI?: string };
};
export type IdentityFormSubmitter = {
  getAttribute?: (name: string) => string | null;
};
type PageLocation = Pick<Location, "href" | "origin">;
export type IdentityFormPolicy = Pick<
  PixelConfig["identityCapture"],
  "enabled" | "markedForms"
> & {
  discovery?: Pick<IdentityDiscoveryConfig, "formMode">;
};

function readAttribute(
  element:
    | { getAttribute?: (name: string) => string | null }
    | null
    | undefined,
  name: string,
) {
  try {
    return element?.getAttribute?.(name) ?? null;
  } catch {
    return null;
  }
}

export function isIdentityFormMarked(form: Form) {
  return readAttribute(form, "data-prospix-identify") === "true";
}

/** Shared form eligibility; no values, network calls or plugin registration. */
export function isSupportedIdentityForm(
  form: Form,
  submitter?: IdentityFormSubmitter | null,
  pageLocation: PageLocation | undefined = typeof location === "undefined"
    ? undefined
    : location,
) {
  if (!pageLocation) return true;
  const action =
    readAttribute(submitter, "formaction") ?? readAttribute(form, "action");
  if (!action) return true;
  try {
    const destination = new URL(
      action,
      form.ownerDocument?.baseURI || pageLocation.href,
    );
    // A mailto action still uses controls in the installed document. No email
    // draft or external document is read by the pixel.
    return (
      destination.protocol === "mailto:" ||
      ((destination.protocol === "https:" ||
        destination.protocol === "http:") &&
        destination.origin === pageLocation.origin)
    );
  } catch {
    return false;
  }
}

export function resolveIdentityFormPolicyBlockReason(
  policy: IdentityFormPolicy,
  form: Form,
  submitter?: IdentityFormSubmitter | null,
  pageLocation?: PageLocation,
): "unsupported_source" | "workspace_policy" | undefined {
  if (!isSupportedIdentityForm(form, submitter, pageLocation))
    return "unsupported_source";
  if (
    !policy.enabled ||
    readAttribute(form, "data-prospix-identify") === "false"
  )
    return "workspace_policy";
  const mode = policy.discovery?.formMode ?? "automatic";
  if (
    mode === "disabled" ||
    (mode === "explicit" &&
      (!policy.markedForms || !isIdentityFormMarked(form)))
  )
    return "workspace_policy";
  return undefined;
}
